import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
  diffItems,
} from '@spotify/plugin-confidence-activity-react';

import { pluralize, shouldSkipDetailedRender } from './utils/extensionHelpers';
import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

const tagIsHoldback = (tag: string) => {
  const [surface, , holdback] = tag.split('/');
  return (
    !!surface &&
    !!holdback &&
    holdback === 'holdbacks' &&
    surface === 'surfaces'
  );
};

const tagIsExclusivityGroup = (tag: string) => {
  const [surface, , exclusivityGroups] = tag.split('/');
  return (
    !!surface &&
    !!exclusivityGroups &&
    exclusivityGroups === 'exclusivityGroups' &&
    surface === 'surfaces'
  );
};

interface SegmentData {
  allocation?: {
    exclusiveTo?: string[];
  };
}

const getExclusiveTo = (value: unknown): string[] => {
  // The value is the segment object directly at /module_data/flags/segment
  const segment = value as SegmentData | null;
  return segment?.allocation?.exclusiveTo ?? [];
};

const categorizeTags = (tags: string[]) => {
  return {
    exclusivityGroups: tags.filter(tagIsExclusivityGroup),
    holdbacks: tags.filter(tagIsHoldback),
  };
};

const SegmentChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldExclusiveTo = getExclusiveTo(change.oldValue);
  const newExclusiveTo = getExclusiveTo(change.newValue);

  const { added, removed } = diffItems(
    oldExclusiveTo,
    newExclusiveTo,
    tag => tag,
  );

  const addedCategorized = categorizeTags(added);
  const removedCategorized = categorizeTags(removed);

  // Skip rendering when tag shows single-item details
  if (shouldSkipDetailedRender(added.length, removed.length)) {
    return null;
  }

  return (
    <>
      {addedCategorized.exclusivityGroups.length > 0 && (
        <ActivityItem.Section
          label={`Added ${addedCategorized.exclusivityGroups.length} ${pluralize(addedCategorized.exclusivityGroups.length, 'exclusivity group')}`}
        >
          {addedCategorized.exclusivityGroups.map(tag => (
            <ActivityTagItem
              key={tag}
              tagLabel={extractLastNameComponent(tag) || tag}
              newValue=""
              ariaLabel="Exclusivity group"
            />
          ))}
        </ActivityItem.Section>
      )}

      {addedCategorized.holdbacks.length > 0 && (
        <ActivityItem.Section
          label={`Added ${addedCategorized.holdbacks.length} ${pluralize(addedCategorized.holdbacks.length, 'holdback')}`}
        >
          {addedCategorized.holdbacks.map(tag => (
            <ActivityTagItem
              key={tag}
              tagLabel={extractLastNameComponent(tag) || tag}
              newValue=""
              ariaLabel="Holdback"
            />
          ))}
        </ActivityItem.Section>
      )}

      {removedCategorized.exclusivityGroups.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${removedCategorized.exclusivityGroups.length} ${pluralize(removedCategorized.exclusivityGroups.length, 'exclusivity group')}`}
        >
          {removedCategorized.exclusivityGroups.map(tag => (
            <ActivityTagItem
              key={tag}
              tagLabel={extractLastNameComponent(tag) || tag}
              oldValue=""
              ariaLabel="Exclusivity group"
            />
          ))}
        </ActivityItem.Section>
      )}

      {removedCategorized.holdbacks.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${removedCategorized.holdbacks.length} ${pluralize(removedCategorized.holdbacks.length, 'holdback')}`}
        >
          {removedCategorized.holdbacks.map(tag => (
            <ActivityTagItem
              key={tag}
              tagLabel={extractLastNameComponent(tag) || tag}
              oldValue=""
              ariaLabel="Holdback"
            />
          ))}
        </ActivityItem.Section>
      )}
    </>
  );
};

const segmentChangePredicate = ({ change }: { change: any }) => {
  if (change.fieldPath !== '/module_data/flags/segment') {
    return false;
  }

  const oldExclusiveTo = getExclusiveTo(change.oldValue);
  const newExclusiveTo = getExclusiveTo(change.newValue);

  const { added, removed } = diffItems(
    oldExclusiveTo,
    newExclusiveTo,
    tag => tag,
  );

  // Only show if there are holdback or exclusivity group changes
  const addedCategorized = categorizeTags(added);
  const removedCategorized = categorizeTags(removed);

  const hasRelevantChanges =
    addedCategorized.holdbacks.length > 0 ||
    addedCategorized.exclusivityGroups.length > 0 ||
    removedCategorized.holdbacks.length > 0 ||
    removedCategorized.exclusivityGroups.length > 0;

  return hasRelevantChanges;
};

const segmentChangeTitle = (change: any) => {
  const oldExclusiveTo = getExclusiveTo(change.oldValue);
  const newExclusiveTo = getExclusiveTo(change.newValue);

  const { added, removed } = diffItems(
    oldExclusiveTo,
    newExclusiveTo,
    tag => tag,
  );

  const addedCategorized = categorizeTags(added);
  const removedCategorized = categorizeTags(removed);

  // Single item changes
  if (added.length === 1 && removed.length === 0) {
    if (addedCategorized.exclusivityGroups.length === 1) {
      return 'Added exclusivity group';
    }
    if (addedCategorized.holdbacks.length === 1) {
      return 'Added holdback';
    }
  }

  if (removed.length === 1 && added.length === 0) {
    if (removedCategorized.exclusivityGroups.length === 1) {
      return 'Exclusivity group removed';
    }
    if (removedCategorized.holdbacks.length === 1) {
      return 'Holdback removed';
    }
  }

  const hasHoldbackChanges =
    addedCategorized.holdbacks.length > 0 ||
    removedCategorized.holdbacks.length > 0;
  const hasExclusivityGroupChanges =
    addedCategorized.exclusivityGroups.length > 0 ||
    removedCategorized.exclusivityGroups.length > 0;

  if (hasHoldbackChanges || hasExclusivityGroupChanges) {
    return 'Updated coordination';
  }

  return 'Updated surfaces';
};

export const SEGMENT_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'segment-change',
  priority: 10,
  predicate: segmentChangePredicate,
  title: segmentChangeTitle,
  tag: change => {
    const oldExclusiveTo = getExclusiveTo(change.oldValue);
    const newExclusiveTo = getExclusiveTo(change.newValue);

    const { added, removed } = diffItems(
      oldExclusiveTo,
      newExclusiveTo,
      tag => tag,
    );

    const addedCategorized = categorizeTags(added);
    const removedCategorized = categorizeTags(removed);

    if (
      added.length === 1 &&
      removed.length === 0 &&
      addedCategorized.exclusivityGroups.length === 1
    ) {
      return {
        tagLabel:
          extractLastNameComponent(addedCategorized.exclusivityGroups[0]) ||
          addedCategorized.exclusivityGroups[0],
        ariaLabel: 'Exclusivity group',
      };
    }

    if (
      added.length === 1 &&
      removed.length === 0 &&
      addedCategorized.holdbacks.length === 1
    ) {
      return {
        tagLabel:
          extractLastNameComponent(addedCategorized.holdbacks[0]) ||
          addedCategorized.holdbacks[0],
        ariaLabel: 'Holdback',
      };
    }

    if (
      removed.length === 1 &&
      added.length === 0 &&
      removedCategorized.exclusivityGroups.length === 1
    ) {
      return {
        tagLabel:
          extractLastNameComponent(removedCategorized.exclusivityGroups[0]) ||
          removedCategorized.exclusivityGroups[0],
        ariaLabel: 'Exclusivity group',
      };
    }

    if (
      removed.length === 1 &&
      added.length === 0 &&
      removedCategorized.holdbacks.length === 1
    ) {
      return {
        tagLabel:
          extractLastNameComponent(removedCategorized.holdbacks[0]) ||
          removedCategorized.holdbacks[0],
        ariaLabel: 'Holdback',
      };
    }

    return null;
  },
  render: SegmentChangeRender,
});

// Export for testing
export { segmentChangePredicate, segmentChangeTitle };
